// Datos SEO para las páginas públicas de cada paquetería (linkbuilding interno).
// Genera contenido único por carrier a partir de su metadata + copy curado.
import { CARRIERS } from "@/lib/carriers";

// Slugs amigables y estables por carrier (URL: /paqueterias/<slug>).
const SLUG_MAP = {
  "DHL": "dhl",
  "FedEx": "fedex",
  "UPS": "ups",
  "Estafeta": "estafeta",
  "Paquetexpress": "paquetexpress",
  "Redpack": "redpack",
  "Quetzal": "quetzal",
  "Nuvee": "nuvee",
  "Carssa": "carssa",
  "T1negocios": "t1-negocios",
  "Guatex": "guatex",
  "99Minutos": "99minutos",
  "Drive": "drive-express",
  "Afimex": "afimex",
  "Sendex": "sendex",
  "Ivoy": "ivoy",
  "Imile": "imile",
  "FletesMexico": "fletes-mexico",
  "TresGuerras": "tres-guerras",
  "AMPM": "ampm",
  "Coordinadora": "coordinadora",
  "FedExFreight": "fedex-freight",
  "Almex": "almex",
  "BigLogistics": "big-logistics",
  "Castores": "castores",
  "Entrega": "entrega",
  "Envia": "envia",
  "PuntoPost": "puntopost",
  "Uber": "uber",
  "J&T": "jt-express",
  "Yaslan": "yaslan",
  "BHGC": "bhgc-nexus",
};

// Copy curado breve por carrier para enriquecer la descripción (único por marca).
const CARRIER_BLURB = {
  "DHL": "la paquetería líder en envíos internacionales y express",
  "FedEx": "envíos express con cobertura nacional e internacional",
  "UPS": "logística confiable para paquetes y documentos",
  "Estafeta": "la paquetería mexicana con mayor cobertura nacional",
  "Paquetexpress": "paquetería regional con fuerte presencia en el Bajío y occidente",
  "Redpack": "envíos económicos con cobertura nacional en México",
  "Quetzal": "paquetería enfocada en zonas metropolitanas",
  "Nuvee": "entregas urbanas same-day en ciudades principales",
  "Carssa": "paquetería regional con cobertura extendida",
  "T1negocios": "soluciones de envío para negocios y PYMES",
  "Guatex": "paquetería con cobertura en zonas específicas",
  "99Minutos": "entregas same-day y next-day en zonas urbanas",
  "Drive": "paquetería express para e-commerce",
  "Afimex": "paquetería regional del centro del país",
  "Sendex": "envíos con cobertura nacional para empresas",
  "Ivoy": "mensajería express same-day en CDMX y zona metropolitana",
  "Imile": "paquetería de última milla para e-commerce",
  "FletesMexico": "fletes y carga pesada a nivel nacional",
  "TresGuerras": "paquetería regional del Bajío mexicano",
  "AMPM": "envíos de última milla en zonas urbanas",
  "Coordinadora": "paquetería regional con cobertura extendida",
  "FedExFreight": "fletes y carga pesada de FedEx",
  "Almex": "paquetería con cobertura nacional en México",
  "BigLogistics": "logística de carga y fletes pesados",
  "Castores": "paquetería del occidente con cobertura nacional",
  "Entrega": "entregas regionales y nacionales",
  "Envia": "agregador de paqueterías con tarifas consolidadas",
  "PuntoPost": "envíos urbanos con cobertura limitada",
  "Uber": "envíos same-day integrados a Uber",
  "J&T": "paquetería de origen asiático con expansión en México",
  "Yaslan": "paquetería con cobertura regional",
  "BHGC": "la marca propia de BHGC Nexus que selecciona la mejor tarifa por nivel de servicio",
};

export function getCarrierSlug(id) {
  return SLUG_MAP[id] || String(id || "").toLowerCase();
}

export function getCarrierBySlug(slug) {
  return CARRIERS.find(c => getCarrierSlug(c.id) === slug);
}

// Sitios oficiales de las paqueterías (referencias salientes para linkbuilding
// y señales de autoridad). Solo carriers con dominio oficial confirmado.
const OFFICIAL_URL = {
  "DHL": "https://www.dhl.com/mx-es/home.html",
  "FedEx": "https://www.fedex.com/en-mx/home.html",
  "UPS": "https://www.ups.com/mx/es/Home.page",
  "Estafeta": "https://www.estafeta.com/",
  "Paquetexpress": "https://www.paquetexpress.com.mx/",
  "Redpack": "https://www.redpack.com.mx/",
  "99Minutos": "https://99minutos.com/",
  "Ivoy": "https://ivoy.mx/",
  "Imile": "https://www.imile.com/",
  "TresGuerras": "https://www.tresguerras.com.mx/",
  "Castores": "https://www.castores.com.mx/",
  "Uber": "https://www.uber.com/mx/es/",
  "J&T": "https://www.jtexpress.mx/",
};

// Palabras clave por carrier para alinear con las páginas oficiales.
const CARRIER_KEYWORDS = {
  "DHL": "cotizar DHL México, rastrear DHL, guía DHL, DHL Express, envío internacional DHL, guía prepagada DHL, DHL México tarifa",
  "FedEx": "cotizar FedEx México, rastrear FedEx, guía FedEx, FedEx Express, envío internacional FedEx, guía prepagada FedEx",
  "UPS": "cotizar UPS México, rastrear UPS, guía UPS, envío internacional UPS, UPS logística B2B, guía prepagada UPS",
  "Estafeta": "cotizar Estafeta, rastrear Estafeta, guía Estafeta, Estafeta envíos nacionales, última milla Estafeta, guía prepagada Estafeta",
  "Paquetexpress": "cotizar Paquetexpress, rastrear Paquetexpress, guía prepagada Paquetexpress, Paquetexpress internacional, Paquetexpress Bajío",
  "Redpack": "cotizar Redpack, rastrear Redpack, guía Redpack, Redpack envíos económicos, guía prepagada Redpack",
  "Quetzal": "cotizar Quetzal, rastrear Quetzal, guía Quetzal, Quetzal paquetería metropolitana",
  "Nuvee": "cotizar Nuvee, rastrear Nuvee, entrega same day Nuvee, Nuvee envíos urbanos",
  "Carssa": "cotizar Carssa, rastrear Carssa, guía Carssa, Carssa paquetería regional",
  "T1negocios": "cotizar T1 Negocios, rastrear T1 Negocios, guía T1 Negocios, envíos PYMES T1 Negocios",
  "Guatex": "cotizar Guatex, rastrear Guatex, guía Guatex, Guatex paquetería",
  "99Minutos": "cotizar 99 Minutos, rastrear 99 Minutos, entrega same day 99 Minutos, 99 Minutos next day",
  "Drive": "cotizar Drive Express, rastrear Drive Express, guía Drive Express, Drive paquetería e-commerce",
  "Afimex": "cotizar Afimex, rastrear Afimex, guía Afimex, Afimex paquetería centro México",
  "Sendex": "cotizar Sendex, rastrear Sendex, guía Sendex, Sendex envíos empresas",
  "Ivoy": "cotizar Ivoy, rastrear Ivoy, mensajería express same day Ivoy CDMX, Ivoy express",
  "Imile": "cotizar iMile, rastrear iMile, última milla iMile e-commerce, iMile envíos",
  "FletesMexico": "cotizar Fletes México, fletes pesados México, carga Fletes México, flete nacional",
  "TresGuerras": "cotizar Tres Guerras, rastrear Tres Guerras, guía Tres Guerras, Tres Guerras Bajío",
  "AMPM": "cotizar AMPM, rastrear AMPM, última milla AMPM, AMPM envíos urbanos",
  "Coordinadora": "cotizar Coordinadora, rastrear Coordinadora, guía Coordinadora, Coordinadora paquetería regional",
  "FedExFreight": "cotizar FedEx Freight, fletes FedEx, carga pesada FedEx Freight",
  "Almex": "cotizar Almex, rastrear Almex, guía Almex, Almex paquetería nacional",
  "BigLogistics": "cotizar Big Logistics, fletes Big Logistics, carga pesada Big Logistics",
  "Castores": "cotizar Castores, rastrear Castores, guía Castores, Castores paquetería occidente",
  "Entrega": "cotizar Entrega, rastrear Entrega, guía Entrega, Entrega paquetería regional",
  "Envia": "cotizar Envia, rastrear Envia, guía Envia, Envia agregador paqueterías",
  "PuntoPost": "cotizar PuntoPost, rastrear PuntoPost, guía PuntoPost, PuntoPost envíos urbanos",
  "Uber": "cotizar Uber envíos, rastrear Uber, Uber same day, Uber logística urbana",
  "J&T": "cotizar J&T Express, rastrear J&T, guía J&T Express, J&T Express México, J&T e-commerce",
  "Yaslan": "cotizar Yaslan, rastrear Yaslan, guía Yaslan, Yaslan paquetería regional",
  "BHGC": "cotizar BHGC Nexus, guía BHGC Nexus, BHGC Nexus Logistics, mejor tarifa paquetería México",
};

// Construye el contenido SEO de la página de un carrier.
export function buildCarrierSeo(carrier) {
  const name = carrier.name;
  const slug = getCarrierSlug(carrier.id);
  const blurb = CARRIER_BLURB[carrier.id] || "una paquetería integrada a BHGC Nexus";
  const scope = carrier.international ? "nacional e internacional" : "nacional";
  const trackLine = carrier.trackingBase
    ? `Rastrea tus envíos de ${name} con el número de guía directamente en su rastreador oficial o desde el rastreo centralizado de BHGC Nexus.`
    : `Consulta el estatus de tus envíos de ${name} desde el rastreo centralizado de BHGC Nexus con línea de tiempo y mapa.`;
  const officialUrl = OFFICIAL_URL[carrier.id];
  const trackUrl = carrier.trackingBase || undefined;
  const references = [];
  if (officialUrl) references.push({ label: `Sitio oficial de ${name}`, url: officialUrl });
  if (trackUrl) references.push({ label: `Rastreador oficial de ${name}`, url: trackUrl });

  // Copywriting SEO por tipo de carrier (fortalezas, casos de uso y
  // posicionamiento de marca). El contenido es único por paquetería para
  // evitar canibalización entre páginas y reforzar palabras clave de marca.
  const isFreight = carrier.id === "FletesMexico" || carrier.id === "FedExFreight" || carrier.id === "BigLogistics";
  const isUrban = ["Nuvee", "99Minutos", "Ivoy", "AMPM", "Imile", "Uber", "Drive", "PuntoPost"].includes(carrier.id);
  const isRegional = ["Quetzal", "Carssa", "Afimex", "TresGuerras", "Castores", "Yaslan", "Entrega", "Guatex", "Almex", "Coordinadora", "Sendex", "T1negocios"].includes(carrier.id);
  const isInternational = !!carrier.international || ["DHL", "FedEx", "UPS", "J&T"].includes(carrier.id);

  const strengths = [];
  if (isInternational) strengths.push(`Cobertura internacional y express para enviar dentro y fuera de México con ${name}.`);
  else strengths.push(`Cobertura nacional con ${name} y cálculo automático de zonas postales.`);
  if (isUrban) strengths.push(`Entregas same-day y next-day en zonas urbanas con ${name}.`);
  if (isFreight) strengths.push(`Fletes y carga pesada a nivel nacional con ${name}.`);
  if (isRegional) strengths.push(`Presencia regional fuerte con ${name} para rutas del interior de la república.`);
  strengths.push(`Cotización en tiempo real con peso volumétrico y descuentos aplicados automáticamente.`);
  strengths.push(`Generación de guía prepagada de ${name} con etiqueta PDF lista para imprimir.`);

  const useCases = [];
  useCases.push(`Tiendas en línea y e-commerce que envían con ${name} a diario.`);
  if (isFreight) useCases.push(`Empresas que mueven carga pesada, pallets o fletes con ${name}.`);
  else useCases.push(`PYMES que buscan la mejor tarifa de ${name} comparando contra otras paqueterías.`);
  useCases.push(`Vendedores que necesitan rastrear varios envíos de ${name} desde un solo panel.`);

  const positioning = `BHGC Nexus Logistics es la plataforma multicarrier que reúne a ${name} y a más de 30 paqueterías en una sola cotización. Al integrar a ${name} (${blurb}), te entregamos su tarifa real con peso volumétrico, zonas postales y descuentos calculados al instante, y te mostramos alternativas cuando ${name} no es la opción más barata o rápida para tu ruta. Así posicionas tu operación logística con tarifas transparentes, saldo prepago, sin contratos y sin mínimo de envíos.`;

  return {
    slug,
    name,
    metaTitle: `${name} en México: cotizar y rastrear envíos | BHGC Nexus Logistics`,
    metaDescription: `${name} en México: cotiza y genera guías con BHGC Nexus Logistics. ${blurb}. Compara tarifas, descarga tu etiqueta PDF y rastrea tus envíos ${scope}.`,
    keywords: CARRIER_KEYWORDS[carrier.id],
    officialUrl,
    trackUrl,
    references,
    h1: `${name}: cotizar, generar guías y rastrear paquetes en México`,
    intro: `BHGC Nexus Logistics integra a ${name}, ${blurb}, para que cotices, generes y rastrees tus envíos ${scope} desde una sola plataforma. Compara la tarifa de ${name} contra las demás paqueterías de México, elige la mejor opción por precio o rapidez y descarga tu etiqueta PDF sin salir de la plataforma. El peso volumétrico, las zonas postales y tus descuentos se calculan solos.`,
    services: [
      `Cotización en tiempo real de ${name} con cálculo automático de peso volumétrico y zonas.`,
      `Generación de guías prepagadas de ${name} con etiqueta PDF lista para imprimir.`,
      `${trackLine}`,
      `Agenda de recolección de ${name} en la dirección y ventana horaria que elijas.`,
      `Cancelación, clonación y duplicación de envíos de ${name} desde Mis Envíos.`,
    ],
    coverage: `Cobertura ${scope} con ${name}, con cálculo de zona extendida y plus zona para tarifar con precisión las rutas de difícil acceso y los códigos postales fuera de zona metropolitana.`,
    whyBhgc: `Al cotizar ${name} dentro de BHGC Nexus comparas su tarifa contra más de 30 paqueterías en una sola petición. Si ${name} no es la opción más económica o rápida para tu ruta, la plataforma te muestra alternativas y tú eliges. Todo con saldo prepago, sin contratos y sin mínimo de envíos.`,
    faqs: [
      { q: `¿Cómo cotizo un envío con ${name}?`, a: `Ingresa a Cotizar en BHGC Nexus, captura el código postal de origen y destino, el peso y las dimensiones del paquete. La plataforma muestra la tarifa de ${name} junto a las de las demás paqueterías para que elijas la más conveniente por precio o rapidez.` },
      { q: `¿Puedo rastrear mi paquete de ${name}?`, a: `Sí. Desde Rastreo ingresa el número de guía de ${name} y verás el estatus, la línea de tiempo de eventos y la ubicación estimada en el mapa. ${trackLine}` },
      { q: `¿Cómo genero una guía prepagada de ${name}?`, a: `Al elegir ${name} en el cotizador, completa los datos de remitente y destinatario y pulsa Generar guía. El costo se descuenta de tu saldo prepago y descargas la etiqueta PDF al instante para imprimir y entregar al repartidor.` },
      { q: `¿${name} tiene cobertura en mi zona?`, a: `BHGC Nexus calcula la zona postal a partir del código postal de destino. Si ${name} no cubre la ruta o su tarifa es alta por zona extendida, verás otras paqueterías con cobertura y mejor precio para el mismo envío.` },
    ],
    strengths,
    useCases,
    positioning,
  };
}